import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    ActivatedRoute,
    Router,
} from '@angular/router';
import { AppStateFacade } from '@app-state';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';
import { unsubscribe } from '@utils/object.utils';
import UiValidators, { UI_PASSWORD_MAX_LENGTH } from '@validators';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    Subscription,
} from 'rxjs';
import {
    finalize,
    switchMap,
    tap,
} from 'rxjs/operators';

@Component({
    templateUrl: 'password-expired.page.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordExpiredPageComponent implements OnInit, OnDestroy {
    public loading$ = new BehaviorSubject(false);
    public form: FormGroup;

    private _userId?: number;
    private _routeParamSub!: Subscription;

    constructor(
        fb: FormBuilder,
        private _route: ActivatedRoute,
        private _router: Router,
        private _authService: AuthService,
        private _translateService: TranslateService,
        private _snack: UiSnackBarService,
        private _appState: AppStateFacade,
    ) {
        this.form = fb.group({
            'currentPassword': ['', Validators.compose([
                Validators.required,
                Validators.maxLength(UI_PASSWORD_MAX_LENGTH),
            ])],
            'newPassword': ['', Validators.compose([
                Validators.required,
                Validators.maxLength(UI_PASSWORD_MAX_LENGTH),
            ])],
            'confirmPassword': [''],
        }, {
                validator: UiValidators.confirmPassword('newPassword', 'confirmPassword'),
            });
    }

    ngOnInit() {
        this._routeParamSub = this._route.queryParams.subscribe(qParams => {
            this._userId = parseInt(qParams.userId, 10);
            if (!this._userId) {
                this._router.navigate(['/account/login']);
            }
        });
    }

    ngOnDestroy() {
        unsubscribe(
            this._routeParamSub,
        );
    }

    public onSubmit(event: Event) {
        event.preventDefault();
        if (!this.form.valid || !this._userId) {
            return;
        }
        this.loading$.next(true);

        const payload = cloneDeep(this.form.value);
        payload.userId = this._userId;
        delete payload['confirmPassword'];
        this._authService.changeExpiredPassword(payload)
            .pipe(
                finalize(
                    () => this.loading$.next(false),
                ),
                tap(() => this._snack.success(this._translateService.instant('password_expired_success_message'))),
                switchMap(() => this._appState.user.ensureCorrectIdentity()),
                switchMap(() => this._appState.user.fetch()),
            )
            .subscribe(() => {
                const requestedRoute = this._router.routerState.snapshot.root.queryParams['returnUrl'];
                const { returnUrl, navigationParams } = this._authService.extractReturnUrl(requestedRoute);
                this._router.navigate([returnUrl], navigationParams);
            });
    }
}
